import { TableCell, TableRow } from "@mui/material";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import { ContentKey } from "../../../../features/talentProfile/TalentDetails";
import { DataItem, EmptyStateText } from "./FormTable";
import FormTableButtons from "./FormTableButtons";
import FormTableCell from "./FormTableCell";
import { StyledTableBody } from "./styles";

type Props = {
  data: DataItem[];
  translationKeys?: string[];
  valueMaxLines?: number;
  disableEditRow?: boolean;
  disableEditOnly?: boolean;
  addButtonLabel?: string;
  contentKey?: string;
  handleOpenEditModal: (content: ContentKey, id?: string) => void;
  handleDelete?: (id: string) => void;
  handleToggle?: (id: string) => void;
  columnWidths?: string[];
  stripedTable?: boolean;
  emptyStateText?: EmptyStateText;
};

const FormTableBody = ({
  data,
  translationKeys,
  valueMaxLines,
  columnWidths,
  disableEditRow,
  disableEditOnly,
  handleOpenEditModal,
  handleDelete,
  handleToggle,
  contentKey,
  stripedTable,
  emptyStateText,
}: Props) => {
  const { t } = useTranslation();

  const allTranslations = useAppSelector((state) => state.localized);
  const translationsByKeys = translationKeys
    ? translationKeys.map((key) => allTranslations[key])
    : [];
  const translations = Object.assign({}, ...translationsByKeys);

  if (emptyStateText?.show) {
    return (
      <StyledTableBody stripedTable={stripedTable}>
        <TableRow>
          <TableCell colSpan={100}>
            {emptyStateText?.text || t("talentProfile:noResults")}
          </TableCell>
        </TableRow>
      </StyledTableBody>
    );
  }

  return (
    <StyledTableBody stripedTable={stripedTable}>
      {data.map((obj, index) => {
        const rowValues = Object.values(obj.values);
        return (
          <TableRow
            sx={{
              "& > td": {
                borderBottom: "1px solid rgba(224, 224, 224, 1)",
              },
            }}
            key={`formtable-tablerow-${index}`}
          >
            {rowValues.map((v, index) => {
              const value = typeof v === "string" ? translations[v] || v : v;
              const isLast = index === rowValues.length - 1;
              const isFirst = index === 0;
              const columnWidth = columnWidths && columnWidths[index];

              return (
                <Fragment key={`formtable-tablerow-cell-${index}`}>
                  <FormTableCell
                    valueMaxLines={valueMaxLines}
                    columnWidth={columnWidth}
                    isFirst={isFirst}
                    disabled={obj.disabled}
                    value={value}
                    linkTo={obj.linkTo}
                  />
                  {isLast && (
                    <FormTableButtons
                      obj={obj}
                      disableEditRow={disableEditRow}
                      disableEditOnly={disableEditOnly}
                      handleOpenEditModal={handleOpenEditModal}
                      contentKey={contentKey}
                      handleDelete={handleDelete}
                      handleToggle={handleToggle}
                    />
                  )}
                </Fragment>
              );
            })}
          </TableRow>
        );
      })}
    </StyledTableBody>
  );
};

export default FormTableBody;
